/**
 * Admin App Version Config Page
 *
 * View and edit the force-update / minimum-version configuration
 * for iOS and Android. Changes are written to system_config and
 * take effect on the next app launch — no code deployment required.
 *
 * Backend API: routers/admin_app_version_config.py
 * Database: system_config (migration 200, seeded by migration 1141)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  Save,
  Check,
  AlertTriangle,
  Smartphone,
  ToggleLeft,
  ToggleRight,
  ExternalLink,
  Apple,
  Bot,
} from 'lucide-react';
import { T, card, heading, sectionLabel, iconBox, btnPrimary } from '../utils/adminDesignTokens';
import { API_BASE_URL } from '../utils/apiConfig';
import { storage } from '../services/storage';

// ── Types ────────────────────────────────────────────────────────────────────

interface PlatformConfig {
  min_version: string;
  min_build: number;
  force: boolean;
  message: string;
  store_url: string;
  meta?: Record<string, { updated_at: string | null; updated_by: string | null }>;
}

interface ConfigResponse {
  success: boolean;
  ios: PlatformConfig;
  android: PlatformConfig;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

// ── Component ────────────────────────────────────────────────────────────────

export default function AdminAppVersionConfig() {
  const [config, setConfig] = useState<ConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // Per-platform edit state
  const [iosEdits, setIosEdits] = useState<Partial<PlatformConfig>>({});
  const [androidEdits, setAndroidEdits] = useState<Partial<PlatformConfig>>({});
  const [iosSaveStatus, setIosSaveStatus] = useState<SaveStatus>('idle');
  const [androidSaveStatus, setAndroidSaveStatus] = useState<SaveStatus>('idle');

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await storage.getItem('admin_token');
      const res = await fetch(`${API_BASE_URL}/api/admin/app-version-config`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: ConfigResponse = await res.json();
      setConfig(data);
      // Reset edits to loaded values
      setIosEdits({});
      setAndroidEdits({});
      setLastRefresh(new Date());
    } catch (e: any) {
      setError(e.message || 'Failed to load config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = useCallback(async (platform: 'ios' | 'android') => {
    const edits = platform === 'ios' ? iosEdits : androidEdits;
    const setStatus = platform === 'ios' ? setIosSaveStatus : setAndroidSaveStatus;

    // Build update payload — only include changed fields
    const current = platform === 'ios' ? config?.ios : config?.android;
    if (!current) return;

    const payload: Record<string, any> = { platform };
    let hasChanges = false;

    if (edits.min_version !== undefined && edits.min_version !== current.min_version) {
      payload.min_version = edits.min_version;
      hasChanges = true;
    }
    if (edits.min_build !== undefined && edits.min_build !== current.min_build) {
      payload.min_build = edits.min_build;
      hasChanges = true;
    }
    if (edits.force !== undefined && edits.force !== current.force) {
      payload.force = edits.force;
      hasChanges = true;
    }
    if (edits.message !== undefined && edits.message !== current.message) {
      payload.message = edits.message;
      hasChanges = true;
    }
    if (edits.store_url !== undefined && edits.store_url !== current.store_url) {
      payload.store_url = edits.store_url;
      hasChanges = true;
    }

    if (!hasChanges) return;

    // Confirmation dialog
    const forceLabel = payload.force !== undefined
      ? (payload.force ? ' with FORCE UPDATE enabled' : ' with soft nudge only')
      : '';
    const versionLabel = payload.min_version || current.min_version;
    const confirmed = window.confirm(
      `Setting ${platform.toUpperCase()} minimum to ${versionLabel}${forceLabel}.\n\n` +
      `All users below this version will be prompted to update on their next app launch.\n\n` +
      `Continue?`
    );
    if (!confirmed) return;

    try {
      setStatus('saving');
      const token = await storage.getItem('admin_token');
      const res = await fetch(`${API_BASE_URL}/api/admin/app-version-config`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus('saved');
      setTimeout(() => setStatus('idle'), 3000);
      // Re-fetch to get updated values
      await fetchConfig();
    } catch (e: any) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  }, [config, iosEdits, androidEdits, fetchConfig]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '24px 32px', maxWidth: 960 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ ...heading, fontSize: 24, margin: 0 }}>App Version Config</h1>
          <p style={{ color: T.textSecondary, fontSize: 14, margin: '4px 0 0' }}>
            Manage minimum version enforcement for iOS and Android. Changes take effect on next app launch.
          </p>
        </div>
        <button
          onClick={fetchConfig}
          disabled={loading}
          style={{
            ...btnPrimary,
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 14,
            opacity: loading ? 0.6 : 1,
          }}
        >
          <RefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
          Refresh
        </button>
      </div>

      {/* Info banner */}
      <div style={{
        ...card,
        backgroundColor: T.cedarSubtle,
        marginBottom: 24,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}>
        <div style={iconBox(T.cedarPrimary)}>
          <Smartphone size={14} color="#fff" />
        </div>
        <div style={{ fontSize: 13, color: T.textSecondary, lineHeight: '1.5' }}>
          <strong style={{ color: T.textPrimary }}>How it works:</strong> The app checks this endpoint on every launch.
          If the installed version is below the minimum, users see either a <strong>hard block</strong> (force update modal)
          or a <strong>soft nudge</strong> (dismissible banner). If the endpoint is unreachable, the app loads normally (fail-open).
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div style={{ ...card, backgroundColor: 'var(--status-error-bg-soft)', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={16} color={T.error} />
          <span style={{ color: T.error, fontSize: 14 }}>{error}</span>
        </div>
      )}

      {/* Loading */}
      {loading && !config && (
        <div style={{ textAlign: 'center', padding: 40, color: T.textSecondary }}>
          <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} />
          <p>Loading configuration...</p>
        </div>
      )}

      {/* Platform Cards */}
      {config && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <PlatformCard
            platform="ios"
            label="iOS"
            config={config.ios}
            edits={iosEdits}
            setEdits={setIosEdits}
            saveStatus={iosSaveStatus}
            onSave={() => handleSave('ios')}
          />
          <PlatformCard
            platform="android"
            label="Android"
            config={config.android}
            edits={androidEdits}
            setEdits={setAndroidEdits}
            saveStatus={androidSaveStatus}
            onSave={() => handleSave('android')}
          />
        </div>
      )}

      {/* Footer */}
      {lastRefresh && (
        <p style={{ color: T.textTertiary, fontSize: 12, marginTop: 16, textAlign: 'right' }}>
          Last refreshed: {lastRefresh.toLocaleTimeString()}
        </p>
      )}
    </div>
  );
}

// ── Platform Card ────────────────────────────────────────────────────────────

interface PlatformCardProps {
  platform: 'ios' | 'android';
  label: string;
  config: PlatformConfig;
  edits: Partial<PlatformConfig>;
  setEdits: React.Dispatch<React.SetStateAction<Partial<PlatformConfig>>>;
  saveStatus: SaveStatus;
  onSave: () => void;
}

function PlatformCard({ platform, label, config, edits, setEdits, saveStatus, onSave }: PlatformCardProps) {
  const currentVersion = edits.min_version ?? config.min_version;
  const currentBuild = edits.min_build ?? config.min_build;
  const currentForce = edits.force ?? config.force;
  const currentMessage = edits.message ?? config.message;
  const currentStoreUrl = edits.store_url ?? config.store_url;

  const hasChanges =
    (edits.min_version !== undefined && edits.min_version !== config.min_version) ||
    (edits.min_build !== undefined && edits.min_build !== config.min_build) ||
    (edits.force !== undefined && edits.force !== config.force) ||
    (edits.message !== undefined && edits.message !== config.message) ||
    (edits.store_url !== undefined && edits.store_url !== config.store_url);

  return (
    <div style={card}>
      {/* Card header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ ...heading, fontSize: 18, margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
          {platform === 'ios' ? <Apple size={18} /> : <Bot size={18} />}{label}
        </h2>
        {currentForce && (
          <span style={{
            fontSize: 11,
            fontWeight: 600,
            padding: '3px 10px',
            borderRadius: T.radiusPill,
            backgroundColor: 'var(--status-error-bg-soft)',
            color: T.error,
          }}>
            FORCE ON
          </span>
        )}
      </div>

      {/* Fields */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Min Version */}
        <FieldRow label="Minimum Version">
          <input
            type="text"
            value={currentVersion}
            onChange={(e) => setEdits(prev => ({ ...prev, min_version: e.target.value }))}
            placeholder="1.0.0"
            style={inputStyle}
          />
        </FieldRow>

        {/* Min Build */}
        <FieldRow label="Minimum Build Number">
          <input
            type="number"
            value={currentBuild}
            onChange={(e) => setEdits(prev => ({ ...prev, min_build: parseInt(e.target.value, 10) || 0 }))}
            placeholder="1"
            style={{ ...inputStyle, width: 100 }}
          />
        </FieldRow>

        {/* Force Update Toggle */}
        <FieldRow label="Force Update">
          <button
            onClick={() => setEdits(prev => ({ ...prev, force: !currentForce }))}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: 0,
            }}
          >
            {currentForce ? (
              <ToggleRight size={28} color={T.cedarPrimary} />
            ) : (
              <ToggleLeft size={28} color={T.textTertiary} />
            )}
            <span style={{ fontSize: 13, color: currentForce ? T.cedarPrimary : T.textSecondary, fontWeight: 500 }}>
              {currentForce ? 'Hard block (full-screen modal)' : 'Soft nudge (dismissible banner)'}
            </span>
          </button>
        </FieldRow>

        {/* Message */}
        <FieldRow label="Update Message">
          <textarea
            value={currentMessage}
            onChange={(e) => setEdits(prev => ({ ...prev, message: e.target.value }))}
            rows={2}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 48 }}
          />
        </FieldRow>

        {/* Store URL */}
        <FieldRow label="Store URL">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%' }}>
            <input
              type="text"
              value={currentStoreUrl}
              onChange={(e) => setEdits(prev => ({ ...prev, store_url: e.target.value }))}
              style={{ ...inputStyle, flex: 1 }}
            />
            <a
              href={currentStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: T.cedarPrimary, flexShrink: 0 }}
              title="Test link"
            >
              <ExternalLink size={16} />
            </a>
          </div>
        </FieldRow>
      </div>

      {/* Save button */}
      <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={onSave}
          disabled={!hasChanges || saveStatus === 'saving'}
          style={{
            ...btnPrimary,
            padding: '8px 20px',
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            opacity: (!hasChanges || saveStatus === 'saving') ? 0.5 : 1,
          }}
        >
          {saveStatus === 'saving' ? (
            <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
          ) : saveStatus === 'saved' ? (
            <Check size={14} />
          ) : saveStatus === 'error' ? (
            <AlertTriangle size={14} />
          ) : (
            <Save size={14} />
          )}
          {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : saveStatus === 'error' ? 'Error' : 'Save Changes'}
        </button>
        {hasChanges && saveStatus === 'idle' && (
          <span style={{ fontSize: 12, color: T.warning }}>Unsaved changes</span>
        )}
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ ...sectionLabel, marginBottom: 4, display: 'block' }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: T.radiusMd,
  border: `1px solid ${T.limestone}`,
  fontSize: 14,
  fontFamily: T.fontBody,
  color: T.textPrimary,
  outline: 'none',
  boxSizing: 'border-box',
};
